import { ChangeDetectionStrategy, Component, inject, Input, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'

import { MatDialog } from '@angular/material/dialog'
import { DialogWarnComponent } from '@components/dialogs/dialog-warn/dialog-warn.component'
import { ExportDataService } from '@components/panel/tabs/results-tab/result-manager/export-data/export-data.service'
import { PdfService } from '@components/panel/tabs/results-tab/result-manager/export-data/pdf.service'
import { GEOJSON_FORMAT, RASTER_FORMAT, VECTOR_FORMAT } from '@core/constants/layers.data'
import { CMSession, ResultLayerCM } from '@core/models/calculation-module.model'
import { CMSessionService } from '@core/services/cm-session.service'
import { isNullOrUndefinedString } from '@core/services/core.utilities'
import { LayerResultService } from '@core/services/layer-result.service'
import { UploadedLayersService } from '@core/services/layers-uploaded.service'
import { SessionManagerService } from '@core/services/session-manager.service'
import { ToasterService } from '@core/services/toaster.service'
import { environment } from '@env/environment'
import { ChartConfiguration } from 'chart.js'
import JSZip from 'jszip'
import Keycloak from 'keycloak-js'
import { CardLayerCmComponent } from './card-layer-cm/card-layer-cm.component'

@Component({
  selector: 'app-cm-sessions',
  templateUrl: './cm-session.component.html',
  styleUrl: './cm-session.component.scss',
  imports: [
    // Components
    CardLayerCmComponent,

    //Material
    MatCheckboxModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatCardModule,
  ],
  changeDetection: ChangeDetectionStrategy.Default, // update when unselect all cm session is clicked
})
export class CmSessionComponent {
  @Input({ required: true }) cmSession!: CMSession
  readonly keycloak = inject(Keycloak)
  private readonly _sessionManagerService = inject(SessionManagerService)
  private readonly _dialog = inject(MatDialog)
  private readonly _cmSessionService = inject(CMSessionService)
  private readonly _layerResultService = inject(LayerResultService)
  private readonly _pdfService = inject(PdfService)
  private readonly _exportDataService = inject(ExportDataService)
  private readonly _uploadedLayersService = inject(UploadedLayersService)
  private readonly _toasterService = inject(ToasterService)

  isLoading$ = signal<boolean>(false)
  isConnected$ = signal<boolean>(false)
  isLayerLoading$ = signal<boolean>(false)

  stillSpaceOnAccountState$ = this._uploadedLayersService.stillSpaceOnAccountState$

  allLayerResults$ = this._layerResultService.allLayerResults$
  activeCMSessions$ = this._cmSessionService.activeCMSessions$
  allCharts$ = this._exportDataService.allCharts$

  RASTER_TYPE = RASTER_FORMAT
  GEOJSON_TYPE = GEOJSON_FORMAT
  VECTOR_TYPE = VECTOR_FORMAT

  async ngOnInit() {
    this.isConnected$.set(this.keycloak.authenticated ?? false)
  }

  async selectCMSession(checked: boolean) {
    this.isLayerLoading$.set(true)
    if (checked) {
      this._cmSessionService.enableCMSession(this.cmSession)
    } else {
      this._cmSessionService.disableCMSession(this.cmSession)
    }

    // response of adding a layer on the is in map service
    // this is a dirty fix to prevent to quick click
    await setTimeout(() => {
      this.isLayerLoading$.set(false)
    }, 1000)
  }

  async exportCMFilesToZIP() {
    this.isLoading$.set(true)
    try {
      const allLayers = [
        ...(this.cmSession.results.raster_layers || []).map((layer) => ({
          ...layer,
          format: this.RASTER_TYPE,
        })),
        ...(this.cmSession.results.vector_layers || []).map((layer) => ({
          ...layer,
          format: this.VECTOR_TYPE,
        })),
        ...(this.cmSession.results.geojson_layers || []).map((layer) => ({
          ...layer,
          format: this.GEOJSON_TYPE,
        })),
      ]

      const session_folder = this.cmSession.results.session_folder
        ? this.cmSession.results.session_folder
        : ''
      const cmSessionName = this.cmSession.session_name

      // Layers to download
      const layerFilePromises = allLayers.map(async (layer) => {
        // @TODO: is same code of downloadCMLayer in layer-cm-card, use a viewchildren to call the method of all children

        const extension = this._cmSessionService.defineExtensionLayer(layer.format)
        let fileName = layer.path.split('/').pop() || layer.path
        if (extension && !fileName.endsWith(extension)) {
          fileName += extension
        }

        const response = await this._cmSessionService.exportCMresultFile(
          fileName,
          layer.format,
          this.cmSession.results.session_folder,
        )

        if (!response) return

        return {
          fileName: layer.name + extension,
          blob: response,
        }
      })

      // extra ZIP to download
      const extraZipPromises = (this.cmSession.results.extra_zip_files || []).map(
        async (extraZipFile) => {
          const fileId = extraZipFile.path.split('/').pop()
          if (!fileId) return

          const response = await this._cmSessionService.exportCMresultFile(
            fileId,
            'zip',
            session_folder,
          )
          if (!response) return

          return {
            fileName: `${extraZipFile.name}.zip`,
            blob: response,
          }
        },
      )

      // extra CSV file to download
      const csvPromises =
        (this.cmSession.results.csv_files || []).map(async (csv_file) => {
          const fileId = csv_file.path.split('/').pop()
          if (!fileId) return
          const response = await this._cmSessionService.exportCMresultFile(
            fileId,
            'csv',
            session_folder,
          )
          if (!response) return

          return {
            fileName: csv_file.name,
            blob: response,
          }
        }) || []

      // Combine all files
      const allFiles: any[] = [...layerFilePromises, ...extraZipPromises, ...csvPromises]

      // Indicators csv from front end
      const indicatorsCSV = this._exportDataService.indicatorsToCSV([], [this.cmSession])
      const blobIndicatorsCSV = this._exportDataService.dataToCsv(indicatorsCSV, 'indicators', true)
      const indicatorsFile = {
        fileName: 'indicators.csv',
        blob: blobIndicatorsCSV,
      }
      allFiles.push(indicatorsFile)

      // Charts csv
      if (this.cmSession.results.graphics && this.cmSession.results.graphics.length > 0) {
        this.cmSession.results.graphics.forEach(
          (graph: ChartConfiguration & { title?: string } & { resultTitle?: string }) => {
            graph.resultTitle = this.cmSession.results.name + ' - ' + this.cmSession.session_name
          },
        )
        const chartsCSV = this._exportDataService.chartsToCSV(this.cmSession.results.graphics)
        const blobChartsCSV = this._exportDataService.dataToCsv(chartsCSV, 'indicators', true)
        const chartsFile = {
          fileName: 'charts.csv',
          blob: blobChartsCSV,
        }
        allFiles.push(chartsFile)
      }

      //PDF
      const title = environment.appName + ' results'
      const pdf = await this._pdfService.generateDynamicPdf(
        title,
        indicatorsCSV,
        this.cmSession.results.graphics,
      )
      const pdfFile = {
        fileName: 'results.pdf',
        blob: pdf.output('blob'), // 'blob' is specified to have correct display of images inside pdf
      }
      allFiles.push(pdfFile)

      const currentDate = this._exportDataService.generateDateForFileName()

      Promise.all(allFiles).then((filesForZip) => {
        const zip = new JSZip()
        filesForZip.forEach((file) => {
          if (!file) {
            // Override errors of http request in 1 error
            this._toasterService.openSnackBarError('Error adding some file to zip.')
            return
          }
          zip.file(sanitizeFilename(file.fileName), file.blob)
        })

        zip.generateAsync({ type: 'blob' }).then((content) => {
          const a = document.createElement('a')
          a.href = URL.createObjectURL(content)
          a.download = sanitizeFilename(
            `${environment.appName}_${cmSessionName}_(${currentDate}).zip`,
          )
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(a.href)
        })
      })
    } catch (error) {
      console.error('Export failed', error)
      this._toasterService.openSnackBarError('An error occurred during export.')
    } finally {
      this.isLoading$.set(false)
    }
  }

  async exportExtraOutputFilesCM() {
    this.isLoading$.set(true)

    const cmSessionName = this.cmSession.session_name
    const session_folder = this.cmSession.results.session_folder
      ? this.cmSession.results.session_folder
      : ''

    const zipPromises =
      this.cmSession.results.extra_zip_files?.map(async (extraZipFile) => {
        const fileId = extraZipFile.path.split('/').pop()
        if (!fileId) return
        const response = await this._cmSessionService.exportCMresultFile(
          fileId,
          'zip',
          session_folder,
        )
        if (response) {
          const url = URL.createObjectURL(response) as string
          const a = document.createElement('a')
          a.href = url
          a.download = extraZipFile.name + '.zip'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }
      }) || []

    const csvPromises =
      this.cmSession.results.csv_files?.map(async (csv_file) => {
        const fileId = csv_file.path.split('/').pop()
        if (!fileId) return
        const session_folder = this.cmSession.results.session_folder
          ? this.cmSession.results.session_folder
          : ''
        const response = await this._cmSessionService.exportCMresultFile(
          fileId,
          'csv',
          session_folder,
        )
        if (response) {
          const url = URL.createObjectURL(response) as string
          const a = document.createElement('a')
          a.href = url
          a.download = cmSessionName + '_' + csv_file.name + '.csv' //@todo: check if .csv is necessary cannot find back the cm that return only a csv
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }
      }) || []

    await Promise.all([...zipPromises, ...csvPromises])
    this.isLoading$.set(false)
  }

  async selectCMSessionLayer(checked: boolean, indexLayer: number, layerFormat: string) {
    const isSession = !isNullOrUndefinedString(this.cmSession.id) ? true : false

    // layers in saved CMSession need directory_uuid (if saved)
    const directoryUUID = this.cmSession.results.directory_uuid
    const cmSessionResult = this.cmSession.results
    let layer: ResultLayerCM | undefined
    if (cmSessionResult) {
      if (layerFormat === RASTER_FORMAT) {
        layer = cmSessionResult.raster_layers?.[indexLayer]
      } else if (layerFormat === VECTOR_FORMAT) {
        layer = cmSessionResult.vector_layers?.[indexLayer]
      } else if (layerFormat === GEOJSON_FORMAT) {
        layer = cmSessionResult.geojson_layers?.[indexLayer]
      }
    }

    if (!layer) {
      return
    }

    this._cmSessionService.setLayerLoadingStatus(layer.path, true)

    try {
      switch (layerFormat) {
        case RASTER_FORMAT:
          if (checked) {
            await this._cmSessionService.enableLayerCMSession(
              this.cmSession,
              layer,
              layerFormat,
              isSession,
              directoryUUID,
            )
          } else {
            this._cmSessionService.disableCMSessionLayer(this.cmSession, layer, RASTER_FORMAT)
          }
          break
        case VECTOR_FORMAT:
          if (checked) {
            await this._cmSessionService.enableLayerCMSession(
              this.cmSession,
              layer,
              layerFormat,
              isSession,
              directoryUUID,
            )
          } else {
            this._cmSessionService.disableCMSessionLayer(this.cmSession, layer, VECTOR_FORMAT)
          }
          break
        case GEOJSON_FORMAT:
          if (checked) {
            await this._cmSessionService.enableLayerCMSession(
              this.cmSession,
              layer,
              layerFormat,
              isSession,
              directoryUUID,
            )
          } else {
            this._cmSessionService.disableCMSessionLayer(this.cmSession, layer, GEOJSON_FORMAT)
          }
          break
        default:
          console.warn('This format is not known', layerFormat)
      }
    } finally {
      this._cmSessionService.setLayerLoadingStatus(layer.path, false)
    }
  }

  isLayerLoading(layerPath: string): boolean {
    return this._cmSessionService.layersLoading$()[layerPath] ?? false
  }

  async clickOnDelete() {
    const title = 'Delete cm session'
    const question = `Are you sure you want to delete the calculation module session ${this.cmSession.session_name}?`
    const data = {
      title: title,
      message: question,
    }
    const dialogRef = this._dialog.open(DialogWarnComponent, {
      data: data,
    })

    dialogRef.afterClosed().subscribe(async (deletionConfirm) => {
      if (deletionConfirm) {
        this.isLoading$.set(true)
        await this._cmSessionService.deleteCMSession(this.cmSession)
        this._sessionManagerService.sizeAccount()
        this.isLoading$.set(false)
      }
    })
  }

  async clickOnSave() {
    this.isLoading$.set(true)
    await this._cmSessionService.saveSession(this.cmSession)
    this._sessionManagerService.sizeAccount()
    this.isLoading$.set(false)
  }
}

function sanitizeFilename(filename: string): string {
  return filename.replace(/[\/\\:]/g, '_')
}
